// A month calendar (the date picker on PC) and the floating panel that carries it.
// Phones keep the native `<input type="date">`; callers choose which to show.
//
// Promise:
// - The grid always has 6 weeks starting on Sunday, so the panel never changes height.
// - Days outside min / max cannot be pressed, and the keys stop at the edge of the range.
// - Keys on a day: arrows move by a day / a week, PageUp / PageDown by a month
//   (with Shift by a year), Home / End to the start / end of the week; Enter or
//   Space picks. Only one day is in the Tab order (the focused one, or the 1st
//   of the shown month after the month buttons moved the view).
// - Today is marked by color and a dot, and counted in the given time zone.
// - CalendarPopover: picking a day closes the panel, then calls `onChange` (only
//   when the value changed). `clearLabel` shows a clear button while there is a
//   value (`onChange("")`). Moving the focus out of the panel with Tab closes it.
import { getLocale, m } from "@trameria/i18n";
import type { KeyboardEvent, ReactNode, RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { IconButton } from "./button";
import {
  addDays,
  addMonths,
  clampDate,
  formatDateValue,
  inRange,
  monthGrid,
  monthOf,
  parseDate,
  shiftMonth,
  todayValue,
  toLocalDate,
  type YearMonth,
} from "./calendar-date";
import { Icon } from "./icon";
import { Popover } from "./popover";

/** The small text buttons in the bottom row of a picker ("Set to today", "Remove"). */
export const PICKER_FOOTER_BUTTON =
  "cursor-pointer rounded-md px-[6px] py-[3px] text-[12px] text-muted hover:bg-hover hover:text-body disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent";

/**
 * Focuses `el`, or on the next frame when it cannot take focus yet (the
 * popover stays hidden until it has measured where to go).
 */
export function focusWhenShown(el: HTMLElement | null | undefined): void {
  if (!el) return;
  el.focus({ preventScroll: true });
  if (document.activeElement !== el)
    requestAnimationFrame(() => el.focus({ preventScroll: true }));
}

/** Weekday headers starting on Sunday. 2026-10-04 is a Sunday. */
function weekdayNames(): string[] {
  const f = new Intl.DateTimeFormat(getLocale(), { weekday: "narrow" });
  return Array.from({ length: 7 }, (_, i) =>
    f.format(new Date(2026, 9, 4 + i)),
  );
}

export interface CalendarProps {
  /** The picked day (`"YYYY-MM-DD"`; empty for none). */
  value: string;
  min?: string;
  max?: string;
  onSelect(value: string): void;
  /** Puts the focus on a day right after opening (inside a panel). */
  autoFocus?: boolean;
  /** Extra actions at the right of the bottom row (such as "Remove"). */
  footer?: ReactNode;
  /** IANA time zone that decides "today" (the account's). Undefined = the browser's. */
  timeZone?: string;
}

export function Calendar({
  value,
  min,
  max,
  onSelect,
  autoFocus = false,
  footer,
  timeZone,
}: CalendarProps) {
  const today = todayValue(new Date(), timeZone);
  const selected = parseDate(value) ? value : "";
  // The focused day (roving tabindex). The shown month is this day's month.
  const [focus, setFocus] = useState(() =>
    clampDate(selected || today, min, max),
  );
  const [view, setView] = useState<YearMonth>(
    () => monthOf(focus) ?? { year: 2026, month: 1 },
  );
  // Carry the DOM focus after rendering only when a key moved the focused day.
  const moveFocus = useRef(autoFocus);
  const grid = useRef<HTMLTableElement>(null);

  const days = useMemo(() => monthGrid(view), [view]);
  const weekdays = useMemo(weekdayNames, []);
  const fmt = useMemo(
    () => ({
      title: new Intl.DateTimeFormat(getLocale(), {
        year: "numeric",
        month: "long",
      }),
      full: new Intl.DateTimeFormat(getLocale(), { dateStyle: "full" }),
    }),
    [],
  );
  const title = fmt.title.format(new Date(view.year, view.month - 1, 1));

  // When the focused day is not in the shown month (the month buttons moved
  // only the view), the 1st of the month takes the Tab stop.
  const focusMonth = monthOf(focus);
  const tabStop =
    focusMonth?.year === view.year && focusMonth.month === view.month
      ? focus
      : clampDate(formatDateValue(view.year, view.month, 1), min, max);

  // frontend-development: not a fetch; carries the DOM focus to the focused day.
  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    focusWhenShown(
      grid.current?.querySelector<HTMLButtonElement>(`[data-date="${focus}"]`),
    );
  }, [focus]);

  const goTo = (next: string) => {
    const target = clampDate(next, min, max);
    moveFocus.current = true;
    setFocus(target);
    const ym = monthOf(target);
    if (ym && (ym.year !== view.year || ym.month !== view.month)) setView(ym);
  };

  const onGridKey = (e: KeyboardEvent<HTMLTableElement>) => {
    const from = tabStop;
    const step: Record<string, () => string> = {
      ArrowLeft: () => addDays(from, -1),
      ArrowRight: () => addDays(from, 1),
      ArrowUp: () => addDays(from, -7),
      ArrowDown: () => addDays(from, 7),
      PageUp: () => addMonths(from, e.shiftKey ? -12 : -1),
      PageDown: () => addMonths(from, e.shiftKey ? 12 : 1),
      Home: () => addDays(from, -(toLocalDate(from)?.getDay() ?? 0)),
      End: () => addDays(from, 6 - (toLocalDate(from)?.getDay() ?? 0)),
    };
    const f = step[e.key];
    if (!f) return;
    e.preventDefault();
    goTo(f());
  };

  const shiftView = (n: number) => {
    setView(shiftMonth(view, n));
    setFocus(
      clampDate(
        addMonths(
          formatDateValue(view.year, view.month, parseDate(focus)?.day ?? 1),
          n,
        ),
        min,
        max,
      ),
    );
  };

  return (
    <div className="w-[248px] px-3 py-[6px]">
      <div className="mb-[6px] flex items-center justify-between">
        <span aria-live="polite" className="pl-[4px] text-[13px] text-body">
          {title}
        </span>
        <span className="flex items-center gap-[2px]">
          <IconButton
            label={m.uifence_calendar_prev_month()}
            onClick={() => shiftView(-1)}
          >
            <Icon kind="back" size={14} />
          </IconButton>
          <IconButton
            label={m.uifence_calendar_next_month()}
            onClick={() => shiftView(1)}
          >
            <Icon kind="back" size={14} className="rotate-180" />
          </IconButton>
        </span>
      </div>
      {/* Arrow keys rise from the day buttons (the ones holding focus) to the table. */}
      <table
        ref={grid}
        aria-label={title}
        onKeyDown={onGridKey}
        className="w-full border-collapse"
      >
        <thead>
          <tr>
            {weekdays.map((w, i) => (
              <th
                // biome-ignore lint/suspicious/noArrayIndexKey: a weekday is its position (narrow names repeat letters)
                key={i}
                scope="col"
                className="h-6 p-0 text-center text-[11px] font-normal text-faint"
              >
                {w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, row) => (
            <tr
              // biome-ignore lint/suspicious/noArrayIndexKey: a week is its position
              key={row}
            >
              {days.slice(row * 7, row * 7 + 7).map((d) => {
                const p = parseDate(d);
                const outside = p?.month !== view.month;
                const isSelected = d === selected;
                const isToday = d === today;
                const local = toLocalDate(d);
                return (
                  <td key={d} className="p-0">
                    <button
                      type="button"
                      data-date={d}
                      aria-pressed={isSelected}
                      tabIndex={d === tabStop ? 0 : -1}
                      disabled={!inRange(d, min, max)}
                      aria-label={local ? fmt.full.format(local) : d}
                      aria-current={isToday ? "date" : undefined}
                      onClick={() => onSelect(d)}
                      onFocus={() => {
                        if (d !== focus) setFocus(d);
                      }}
                      className={`relative mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-[12.5px] tabular-nums disabled:cursor-default disabled:opacity-30 ${
                        isSelected
                          ? "bg-accent text-accent-fg"
                          : `hover:bg-hover disabled:hover:bg-transparent ${
                              isToday
                                ? "text-accent"
                                : outside
                                  ? "text-faint"
                                  : "text-body"
                            }`
                      }`}
                    >
                      {p?.day}
                      {/* Color alone is easy to miss for today, so it also gets a dot. */}
                      {isToday && (
                        <span
                          aria-hidden="true"
                          className="absolute bottom-[4px] left-1/2 h-[3px] w-[3px] -translate-x-1/2 rounded-full bg-current"
                        />
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-[4px] flex items-center justify-between border-t border-border pt-[5px]">
        <button
          type="button"
          disabled={!inRange(today, min, max)}
          onClick={() => onSelect(today)}
          className={PICKER_FOOTER_BUTTON}
        >
          {/* "Today" alone reads as "go back to this month"; pressing it picks the day and closes. */}
          {m.uifence_calendar_today()}
        </button>
        {footer}
      </div>
    </div>
  );
}

/** What CalendarPopover and TimePopover take. */
export interface PickerPopoverProps {
  open: boolean;
  /** The element the panel sits next to; focus returns to it after a pick. */
  anchor: RefObject<HTMLElement | null>;
  onClose(): void;
  /** Read aloud for the panel. */
  label?: string;
  align?: "start" | "end";
  value: string;
  min?: string;
  max?: string;
  /** Shown as a button while there is a value; pressing it calls `onChange("")`. */
  clearLabel?: string;
  onChange(value: string): void;
  /** IANA time zone for "today" / "now" (the account's). Undefined = the browser's. */
  timeZone?: string;
}

/**
 * The panel shared by the date and time pickers: a Popover that also closes when
 * Tab moves the focus out of it (left open, it would float over the page).
 * Presses outside are closed by the Popover itself.
 */
export function PickerPanel({
  open,
  anchor,
  onClose,
  label,
  align,
  children,
}: Pick<
  PickerPopoverProps,
  "open" | "anchor" | "onClose" | "label" | "align"
> & {
  children: ReactNode;
}) {
  // Read from the deferred close below, so a panel already closed is not closed twice.
  const live = useRef(open);
  // frontend-development: not a fetch; mirrors `open` for the deferred close.
  useEffect(() => {
    live.current = open;
  }, [open]);
  return (
    <Popover
      open={open}
      anchor={anchor}
      onClose={onClose}
      label={label}
      align={align}
      // The pickers are narrower than a menu; padding lives inside.
      className="min-w-0! py-0!"
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: only receives focusout rising from the buttons inside */}
      <div
        onBlur={(e) => {
          const next = e.relatedTarget;
          if (
            next instanceof Node &&
            !e.currentTarget.contains(next) &&
            !anchor.current?.contains(next)
          )
            // Close after the focus has moved, so whoever reads the focus on
            // close sees where it went.
            setTimeout(() => {
              if (live.current) onClose();
            }, 0);
        }}
      >
        {children}
      </div>
    </Popover>
  );
}

/** The calendar in a floating panel (PC). */
export function CalendarPopover({
  open,
  anchor,
  onClose,
  label,
  align,
  value,
  min,
  max,
  clearLabel,
  onChange,
  timeZone,
}: PickerPopoverProps) {
  // Close first (the closer may look at where the focus is), then hand the focus back.
  const finish = (next: string) => {
    onClose();
    anchor.current?.focus({ preventScroll: true });
    if (next !== value) onChange(next);
  };
  return (
    <PickerPanel
      open={open}
      anchor={anchor}
      onClose={onClose}
      label={label}
      align={align}
    >
      <Calendar
        value={value}
        min={min}
        max={max}
        timeZone={timeZone}
        autoFocus
        onSelect={finish}
        footer={
          clearLabel && value ? (
            <button
              type="button"
              onClick={() => finish("")}
              className={PICKER_FOOTER_BUTTON}
            >
              {clearLabel}
            </button>
          ) : undefined
        }
      />
    </PickerPanel>
  );
}
